/* eslint-disable no-nested-ternary */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { randomId } from '@app/constants';
import { useDebounce } from '@app/utils/hooks/useDebounce.hook';
import { useQuestions } from '@data/hooks';
import { Namespace, OrderDirection } from '@data/index';
import { QuestionOrderBy, QuestionView } from '@data/sdk/match';

export interface QuestionOption extends Partial<QuestionView> {
  createOwn: boolean;
  info: boolean;
  label: string;
  value: string;
}

export const useQuestionsList = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation(Namespace.ADMIN);

  const [searchText, setSearchText] = useState('');

  const searchString = useDebounce(searchText, 500);

  const { data: questions, isFetching: questionsLoading } = useQuestions(
    {
      getQuestionsBody: {
        limit: 10,
        offset: 0,
        searchString: searchString ?? '',
        orderBy: QuestionOrderBy.UpdatedAt,
        orderDirection: OrderDirection.DESC,
        currentVersionOnly: false,
      },
    },
    !!searchString,
  );

  const questionOptions: QuestionOption[] = useMemo(
    () =>
      questions?.items.map((question) => ({
        ...question,
        value: question.id,
        label: question.text,
        info: false,
        createOwn: false,
      })) || [],
    [questions],
  );

  const selectOptions = useMemo(
    () =>
      questionOptions.length
        ? [
            {
              label: t('trial_page.check_if_question_is_on_suggested'),
              value: '',
              info: true,
              createOwn: false,
            },
            ...questionOptions,
            {
              label: searchText,
              value: randomId,
              createOwn: true,
              info: false,
              id: randomId,
              text: searchText,
            },
          ]
        : searchText
        ? [
            {
              label: searchText,
              value: randomId,
              createOwn: true,
              info: false,
              id: randomId,
              text: searchText,
            },
          ]
        : [],
    [questionOptions, searchText, language],
  );

  return { searchText, setSearchText, questionOptions, selectOptions, questionsLoading };
};
